/**
 * PracticeCoach — the AI coaching layer for Practice Performance (CT-025 Phase 4).
 *
 * Bevel's "read it for me" pattern: a plain-language read of the firm sits at the top of the view
 * (CoachBanner), and any read expands into a chat that has the full data context (CoachChat). The
 * caller passes the already-computed dashboard context (buildCoachContext) so we never re-fetch.
 *
 *   <CoachBanner context={ctx} sections={[...]} insights={ins} loading={bool} onAsk={(seed)=>...} />
 *   <CoachChat context={ctx} title="..." seed="..." onClose={...} />   // mounted when asking
 */

import { useState, useRef, useEffect } from 'react'
import { chatAboutPractice } from '../services/practiceAI.js'
import Markdown from './Markdown.jsx'

const INK = '#07272D'
const MUTE = '#6f8893'

/* ── Hero read banner ─────────────────────────────────────────────────────────────────────────── */
export function CoachBanner({ headline, coaching, loading, error, onAsk, onRetry }) {
  return (
    <div style={S.banner}>
      <div style={S.bannerGlow} />
      <div style={S.eyebrowRow}>
        <span style={S.spark}>✦</span>
        <span style={S.eyebrow}>Today's read</span>
      </div>
      {loading ? (
        <div style={S.skeletonWrap}>
          <div style={{ ...S.skel, width: '52%' }} />
          <div style={{ ...S.skel, width: '94%', marginTop: 9 }} />
          <div style={{ ...S.skel, width: '88%', marginTop: 6 }} />
        </div>
      ) : error ? (
        <div style={S.errorWrap}>
          <div style={S.errorTxt}>Could not write the read. {error}</div>
          {onRetry && <button style={S.linkBtn} onClick={onRetry}>Try again</button>}
        </div>
      ) : (
        <>
          {headline && <div style={S.headline}>{headline}</div>}
          {coaching && <div style={S.coaching}>{coaching}</div>}
          <button style={S.askBtn} onClick={() => onAsk && onAsk('')}>Ask about the firm →</button>
        </>
      )}
    </div>
  )
}

/* ── Inline per-section note + ask affordance ─────────────────────────────────────────────────── */
export function CoachNote({ text, loading, onAsk }) {
  if (loading) return <div style={S.noteSkel} />
  if (!text) return null
  return (
    <div style={S.note}>
      <span style={S.noteSpark}>✦</span>
      <span style={S.noteTxt}>{text}</span>
      {onAsk && <button style={S.noteAsk} onClick={() => onAsk(text)} title="Ask about this">ask →</button>}
    </div>
  )
}

/* ── Slide-in chat panel ──────────────────────────────────────────────────────────────────────── */
const SUGGESTIONS = [
  'Who should I rebalance first, and to whom?',
  'Which projects are the real schedule risks right now?',
  'Is anyone underused who could take work?',
  'What is the single most important thing to fix today?',
]

export function CoachChat({ context, title, seed, onClose }) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState(seed || '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const scrollRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => { if (inputRef.current) inputRef.current.focus() }, [])
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight }, [messages, busy])
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function send(text) {
    const q = (text != null ? text : input).trim()
    if (!q || busy) return
    setInput(''); setErr(null)
    const next = [...messages, { role: 'user', content: q }]
    setMessages(next); setBusy(true)
    try {
      const { reply } = await chatAboutPractice(context, next)
      setMessages([...next, { role: 'assistant', content: reply || '(no answer)' }])
    } catch (e) {
      setErr(e.message || 'Could not reach the assistant.')
      setMessages(messages) // roll back the optimistic user turn on hard failure
    } finally { setBusy(false) }
  }

  return (
    <>
      <div style={S.chatBackdrop} onClick={onClose} />
      <div style={S.chatPanel} role="dialog" aria-label="Ask about the firm">
        <div style={S.chatHead}>
          <div>
            <div style={S.chatEyebrow}><span style={S.spark}>✦</span> Practice assistant</div>
            <div style={S.chatTitle}>{title || 'Ask about the firm'}</div>
          </div>
          <button style={S.chatClose} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={S.chatScroll} ref={scrollRef}>
          {messages.length === 0 && (
            <div style={S.empty}>
              <div style={S.emptyTxt}>Ask anything about the firm's workload, schedule risk, or who to move. The assistant has today's numbers.</div>
              <div style={S.suggWrap}>
                {SUGGESTIONS.map((s, i) => <button key={i} style={S.sugg} onClick={() => send(s)}>{s}</button>)}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={m.role === 'user' ? S.userRow : S.botRow}>
              <div style={m.role === 'user' ? S.userBubble : S.botBubble}>
                {m.role === 'user' ? m.content : <Markdown>{m.content}</Markdown>}
              </div>
            </div>
          ))}
          {busy && <div style={S.botRow}><div style={S.botBubble}><span style={S.typing}>thinking…</span></div></div>}
          {err && <div style={S.chatErr}>{err}</div>}
        </div>

        <div style={S.chatInputRow}>
          <textarea
            ref={inputRef}
            style={S.chatInput}
            value={input}
            rows={1}
            placeholder="Ask about the firm…"
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
          />
          <button style={{ ...S.send, ...(busy || !input.trim() ? S.sendOff : {}) }} onClick={() => send()} disabled={busy || !input.trim()}>↑</button>
        </div>
      </div>
    </>
  )
}

const S = {
  banner: { position: 'relative', overflow: 'hidden', background: 'var(--aj-hero-grad)', border: '0.5px solid var(--aj-border)', borderRadius: 18, padding: '20px 22px', marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  bannerGlow: { position: 'absolute', top: -60, right: -40, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(123,108,246,0.16), transparent 70%)', pointerEvents: 'none' },
  eyebrowRow: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 },
  spark: { color: '#7B6CF6', fontSize: 12 },
  eyebrow: { fontFamily: '"Montserrat",sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7a6fb0' },
  headline: { fontFamily: '"DM Sans",sans-serif', fontSize: 19, fontWeight: 600, color: 'var(--aj-text)', lineHeight: 1.25, marginBottom: 7, position: 'relative' },
  coaching: { fontFamily: '"DM Sans",sans-serif', fontSize: 14, lineHeight: 1.55, color: 'var(--aj-text-mid)', maxWidth: 720, position: 'relative' },
  askBtn: { marginTop: 14, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: '#fff', background: INK, border: 'none', borderRadius: 20, padding: '8px 16px', cursor: 'pointer' },
  skeletonWrap: { padding: '2px 0 4px' },
  skel: { height: 13, borderRadius: 6, background: 'linear-gradient(90deg, var(--aj-border), var(--aj-border-mid), var(--aj-border))', backgroundSize: '200% 100%', animation: 'ajShimmer 1.4s ease-in-out infinite' },
  errorWrap: { paddingTop: 2 },
  errorTxt: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-mid)' },
  linkBtn: { marginTop: 6, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: '#3D7DFF', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },

  note: { display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 8 },
  noteSpark: { color: '#7B6CF6', fontSize: 10, lineHeight: '17px', flexShrink: 0 },
  noteTxt: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, lineHeight: 1.45, color: 'var(--aj-text-mid)' },
  noteAsk: { fontFamily: '"DM Mono",monospace', fontSize: 10, color: '#7a6fb0', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 4px', flexShrink: 0, whiteSpace: 'nowrap' },
  noteSkel: { height: 11, width: '70%', borderRadius: 5, marginTop: 9, background: 'linear-gradient(90deg, var(--aj-border), var(--aj-border-mid), var(--aj-border))', backgroundSize: '200% 100%', animation: 'ajShimmer 1.4s ease-in-out infinite' },

  chatBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1200 },
  chatPanel: { position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(440px, 92vw)', background: 'var(--aj-bg-elevated)', zIndex: 1201, display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 32px rgba(0,0,0,0.18)' },
  chatHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 18px', borderBottom: '0.5px solid var(--aj-border)' },
  chatEyebrow: { fontFamily: '"Montserrat",sans-serif', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7a6fb0', display: 'flex', alignItems: 'center', gap: 5 },
  chatTitle: { fontFamily: '"DM Sans",sans-serif', fontSize: 15, fontWeight: 600, color: 'var(--aj-text)', marginTop: 3 },
  chatClose: { fontFamily: '"DM Sans",sans-serif', fontSize: 22, lineHeight: 1, color: 'var(--aj-text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  chatScroll: { flex: 1, overflowY: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 },
  empty: { paddingTop: 6 },
  emptyTxt: { fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.5, color: 'var(--aj-text-mid)', marginBottom: 14 },
  suggWrap: { display: 'flex', flexDirection: 'column', gap: 8 },
  sugg: { textAlign: 'left', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer' },
  userRow: { display: 'flex', justifyContent: 'flex-end' },
  botRow: { display: 'flex', justifyContent: 'flex-start' },
  userBubble: { maxWidth: '84%', background: INK, color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.5, padding: '9px 13px', borderRadius: '14px 14px 4px 14px' },
  botBubble: { maxWidth: '88%', background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.5, padding: '9px 13px', borderRadius: '14px 14px 14px 4px' },
  typing: { color: 'var(--aj-text-muted)', fontStyle: 'italic' },
  chatErr: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#c0392b' },
  chatInputRow: { display: 'flex', alignItems: 'flex-end', gap: 8, padding: '12px 14px', borderTop: '0.5px solid var(--aj-border)' },
  chatInput: { flex: 1, resize: 'none', fontFamily: '"DM Sans",sans-serif', fontSize: 13, lineHeight: 1.4, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 12, padding: '10px 12px', maxHeight: 120, outline: 'none' },
  send: { flexShrink: 0, width: 36, height: 36, borderRadius: '50%', background: INK, color: '#fff', border: 'none', fontSize: 16, cursor: 'pointer' },
  sendOff: { background: 'rgba(7,39,45,0.25)', cursor: 'default' },
}
